import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Help as HelpIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { MasqueradeUser } from '../services/masqueradeService';

interface MasqueradeTableProps {
  users: MasqueradeUser[];
  loading: boolean;
  loadingUserId: number | null;
  onMasquerade: (user: MasqueradeUser) => void;
}

const MasqueradeTable: React.FC<MasqueradeTableProps> = ({
  users,
  loading,
  loadingUserId,
  onMasquerade
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No users found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No users match your current filter criteria.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Firm(s)</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <MasqueradeTableRow
              key={user.id}
              user={user}
              isLoading={loadingUserId === user.id}
              onMasquerade={() => onMasquerade(user)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface MasqueradeTableRowProps {
  user: MasqueradeUser;
  isLoading: boolean;
  onMasquerade: () => void;
}

const MasqueradeTableRow: React.FC<MasqueradeTableRowProps> = ({
  user,
  isLoading,
  onMasquerade
}) => {
  return (
    <TableRow hover>
      <TableCell>
        <UserInfo user={user} />
      </TableCell>
      <TableCell>
        <RoleBadge role={user.role} />
      </TableCell>
      <TableCell>
        <FirmsList firms={user.firms} />
      </TableCell>
      <TableCell>
        <StatusBadge status={user.status} />
      </TableCell>
      <TableCell>
        <MasqueradeAction
          user={user}
          isLoading={isLoading}
          onMasquerade={onMasquerade}
        />
      </TableCell>
    </TableRow>
  );
};

interface UserInfoProps {
  user: MasqueradeUser;
}

const UserInfo: React.FC<UserInfoProps> = ({ user }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main' }}>
      <PersonIcon />
    </Avatar>
    <Box>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {user.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {user.email}
      </Typography>
    </Box>
  </Box>
);

interface RoleBadgeProps {
  role: string;
}

const RoleBadge: React.FC<RoleBadgeProps> = ({ role }) => {
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'client_admin':
        return 'primary';
      case 'client_user':
        return 'success';
      case 'vendor_user':
      case 'vendor_staff':
        return 'secondary';
      case 'vendor_admin':
        return 'info';
      case 'medical_user':
        return 'warning';
      case 'internal_staff':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatRole = (role: string) => {
    return role.replace('_', ' ').toUpperCase();
  };

  return (
    <Chip
      label={formatRole(role)}
      color={getRoleColor(role) as any}
      size="small"
      variant="outlined"
    />
  );
};

interface FirmsListProps {
  firms?: Array<{
    id: number;
    name: string;
    type: string;
  }>;
}

const FirmsList: React.FC<FirmsListProps> = ({ firms }) => {
  if (!firms || firms.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No firms
      </Typography>
    );
  }

  return (
    <Box>
      {firms.map((firm, index) => (
        <Box key={firm.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: index < firms.length - 1 ? 0.5 : 0 }}>
          <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {firm.name}
          </Typography>
          <Chip
            label={firm.type}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      ))}
    </Box>
  );
};

interface StatusBadgeProps {
  status: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: 'success' as const,
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
          label: 'Active'
        };
      case 'inactive':
        return {
          color: 'error' as const,
          icon: <CancelIcon sx={{ fontSize: 16 }} />,
          label: 'Inactive'
        };
      case 'pending':
        return {
          color: 'warning' as const,
          icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
          label: 'Pending'
        };
      default:
        return {
          color: 'default' as const,
          icon: <HelpIcon sx={{ fontSize: 16 }} />,
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

interface MasqueradeActionProps {
  user: MasqueradeUser;
  isLoading: boolean;
  onMasquerade: () => void;
}

const MasqueradeAction: React.FC<MasqueradeActionProps> = ({
  user,
  isLoading,
  onMasquerade
}) => {
  const isDisabled = isLoading || user.status !== 'active';

  return (
    <Tooltip title={isDisabled ? 'User must be active to masquerade' : 'Masquerade as this user'}>
      <span>
        <Button
          onClick={onMasquerade}
          disabled={isDisabled}
          variant="outlined"
          size="small"
          startIcon={isLoading ? <CircularProgress size={16} /> : <LoginIcon />}
          color="primary"
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Switching...' : 'Masquerade'}
        </Button>
      </span>
    </Tooltip>
  );
};

export default MasqueradeTable;
